"use client";

// Réglages commerciaux vus par le navigateur : promotion en cours, offre
// gratuite, périodes d'essai.
//
// POURQUOI UN CONTEXTE : ces réglages étaient des variables d'environnement,
// donc figées dans le bundle au moment du build. Maintenant qu'ils changent
// sans redéploiement, le navigateur doit les demander — mais une seule fois
// pour toute la page, pas une fois par composant (le bandeau, la modale et la
// page des tarifs les lisent tous).
//
// Valeur de départ : celle d'AVANT ce tableau de bord, c'est-à-dire aucune
// promotion. Une page qui affiche une remise (la page des tarifs) reçoit sa
// valeur du serveur en props, donc sans clignotement ; les surfaces
// secondaires (bandeau du haut, modale) apparaissent une fraction de seconde
// après la peinture, exactement comme aujourd'hui où elles attendent déjà le
// chargement de moment-timezone.

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

const DEFAULT_SETTINGS = {
    promotion: null,
    freeOffer: { enabled: true },
    lifetimePromo: { enabled: false },
    trial: {
        pricing: { enabled: true, days: 7 },
        onboarding: { enabled: false, days: 7 },
        teams: { enabled: true, days: 3 },
    },
    grace: { paymentDays: 0 },
};

const CommerceSettingsContext = createContext({
    settings: DEFAULT_SETTINGS,
    loaded: false,
    settled: false,
});

export function CommerceSettingsProvider({ children, initialSettings = null }) {
    const [settings, setSettings] = useState(initialSettings || DEFAULT_SETTINGS);
    // Une valeur venue du serveur est déjà la bonne : inutile de la redemander.
    const [loaded, setLoaded] = useState(!!initialSettings);
    // « La question est tranchée », y compris quand le réseau a échoué : dans ce
    // cas la réponse définitive est « aucune promotion ». À distinguer de
    // `loaded`, qui ne vaut que pour une réponse réellement reçue.
    const [settled, setSettled] = useState(!!initialSettings);
    const hasServerValue = useRef(!!initialSettings);

    useEffect(() => {
        if (hasServerValue.current) return;

        let alive = true;
        fetch("/api/commerce/settings")
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (alive && data) {
                    setSettings(data);
                    setLoaded(true);
                }
            })
            .catch(() => {
                // Réseau indisponible : on garde les valeurs par défaut, qui
                // n'annoncent aucune promotion. Ne jamais promettre une remise
                // qu'on n'est pas sûr de pouvoir appliquer.
            })
            .finally(() => {
                if (alive) setSettled(true);
            });

        return () => {
            alive = false;
        };
    }, []);

    const value = useMemo(
        () => ({ settings, loaded, settled }),
        [settings, loaded, settled],
    );

    return (
        <CommerceSettingsContext.Provider value={value}>
            {children}
        </CommerceSettingsContext.Provider>
    );
}

/**
 * @returns {{settings: object, loaded: boolean, settled: boolean}} les réglages,
 * un drapeau disant si la réponse du serveur est arrivée (utile pour ne pas
 * faire clignoter un compte à rebours) et un drapeau disant si la question est
 * tranchée, échec réseau compris (utile pour ne pas attendre indéfiniment une
 * promotion qui n'arrivera jamais).
 */
export function useCommerceSettings() {
    return useContext(CommerceSettingsContext);
}

/**
 * La promotion applicable, ou null. Raccourci du cas le plus courant.
 */
export function usePromotion() {
    const { settings } = useCommerceSettings();
    return settings.promotion;
}

/**
 * Peut-on annoncer qu'une session est offerte ?
 *
 * Répond false tant que la réponse du serveur n'est pas arrivée : sur une page
 * de formation, mieux vaut afficher le badge « Gratuit » une fraction de
 * seconde plus tard que l'afficher puis le retirer. On ne promet jamais un
 * accès qu'on vient peut-être de fermer.
 */
export function useFreeOfferEnabled() {
    const { settings, loaded } = useCommerceSettings();
    return loaded && settings.freeOffer.enabled;
}
